<template>
  <div class="ant-glass-background radius-0">
    <dynamic-data-table
      :can-delete="true"
      :can-edit="true"
      :is-loading="wkbLibraryTableStatus === 'loading'"
      :table-headers="tableHeaders"
      :table-records="filteredMeasurements"
      table-title=""
      @deleteItem="setupDelete"
      @editItem="setupEdit"
    >
      <template #table-buttons>
        <v-text-field
          v-model="measurementSearch"
          clearable
          dense
          filled
          flat
          hide-details
          placeholder="Type here to search"
          prepend-inner-icon="mdi-magnify"
          style="max-width: 300px !important"
        />
      </template>
      <template #table-actions>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon
              class="ant-icon"
              @click="wkbMeasurementDialogDisplayed = true"
              v-on="on"
            >
              mdi-plus
            </v-icon>
          </template>
          <span>Add document</span>
        </v-tooltip>
      </template>
      <template #item.party="{ value, rowId, item }">
        <v-chip v-if="item.party" class="mx-2" color="white" small
          >{{ value }}
        </v-chip>
        <td v-else>-</td>
      </template>
      <template #item.delivery_folder="{ value, rowId, item }">
        <v-chip v-if="item.delivery_folder" class="mx-2" color="white" small>
          <v-icon class="mr-2" small>mdi-folder-outline</v-icon>
          {{ value }}
        </v-chip>
        <td v-else>-</td>
      </template>
    </dynamic-data-table>
    <v-dialog
      key="wkb-measurement"
      v-model="wkbMeasurementDialogDisplayed"
      max-width="600"
      @click:outside="closeWKBMeasurementDialog"
      @keydown.esc="closeWKBMeasurementDialog"
    >
      <v-card>
        <v-card-title class="justify-center text-uppercase headline">
          {{ wkbMeasurementItem.id ? 'Update' : 'Add' }} Measurement
        </v-card-title>
        <v-divider />
        <div class="px-10 pb-5">
          <v-form ref="create-wkb-measurement-form" @submit.prevent>
            <div class="d-flex">
              <ant-input class="mr-5" label="Number">
                <template #input-field>
                  <v-text-field
                    v-model="wkbMeasurementItem.number"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    :rules="[rules.required]"
                    dense
                    filled
                    hide-details
                    placeholder="Number"
                    type="number"
                  />
                </template>
              </ant-input>

              <ant-input label="Type">
                <template #input-field>
                  <v-select
                    v-model="wkbMeasurementItem.type"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    :items="['leanform', 'document', 'task']"
                    :rules="[rules.required]"
                    dense
                    filled
                    hide-details
                    placeholder="Type"
                  />
                </template>
              </ant-input>
            </div>

            <ant-input label="Name">
              <template #input-field>
                <v-text-field
                  v-model="wkbMeasurementItem.name"
                  :disabled="wkbLibraryTableStatus === 'loading'"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="Name"
                />
              </template>
            </ant-input>

            <ant-input is-optional label="Description">
              <template #input-field>
                <v-textarea
                  v-model="wkbMeasurementItem.description"
                  :disabled="wkbLibraryTableStatus === 'loading'"
                  filled
                  hide-details
                  placeholder="Please add a description here"
                />
              </template>
            </ant-input>

            <div class="d-flex">
              <ant-input class="mr-5 flex-grow-1" label="Sample level">
                <template #input-field>
                  <v-select
                    v-model="wkbMeasurementItem.sample_level"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    :items="[
                      'Project',
                      'Modules',
                      'Bouwblokken',
                      'Types',
                      'Bouwlagen',
                    ]"
                    :rules="[rules.required]"
                    dense
                    filled
                    hide-details
                    placeholder="Sample Level"
                  />
                </template>
              </ant-input>

              <ant-input class="flex-grow-1" label="Party">
                <template #input-field>
                  <v-select
                    v-model="wkbMeasurementItem.party"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    :items="['Kwaliteitsborger', 'DHME']"
                    :rules="[rules.required]"
                    dense
                    filled
                    hide-details
                    placeholder="party"
                  />
                </template>
              </ant-input>
            </div>
            <ant-input class="flex-grow-1" label="Delivery folder">
              <template #input-field>
                <v-combobox
                  v-model="wkbMeasurementItem.delivery_folder"
                  :disabled="wkbLibraryTableStatus === 'loading'"
                  :items="deliveryFolders"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="Folder name"
                />
              </template>
            </ant-input>
          </v-form>
        </div>
        <v-card-actions class="ant-border-top ant-dialog-actions-bg">
          <v-spacer />
          <v-btn
            :disabled="wkbLibraryTableStatus === 'loading'"
            color="primary"
            small
            text
            @click="closeWKBMeasurementDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="wkbLibraryTableStatus === 'loading'"
            :loading="wkbLibraryTableStatus === 'loading'"
            color="primary"
            elevation="0"
            small
            @click="saveOrUpdateWKBMeasurement"
            >{{ wkbMeasurementItem.id ? 'update' : 'create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <delete-dialog
      :dialog="wkbMeasurementDeleteDialogDisplayed"
      title="Are you sure you want to delete this record?"
      @closeDialog="closeWKBMeasurementDialog"
      @deleteAction="deleteWkbMeasurementRecord"
    />
  </div>
</template>

<script>
import DeleteDialog from '@/components/DeleteDialog.vue';
import DynamicDataTable from '@/components/DynamicDataTable.vue';
import { mapGetters } from 'vuex';
import WKBMixin from '@/modules/daiwa-house-modular-europe/WkbLibrary/DhmeWkbLibraryMixin';
import AntInput from '@/components/AntInput.vue';

export default {
  name: 'DhmeWkbMeasurementsLibrary',
  components: { AntInput, DeleteDialog, DynamicDataTable },
  mixins: [WKBMixin],
  data: () => {
    return {
      tableRecords: [],
      tableHeaders: [
        {
          text: 'number',
          value: 'number',
        },
        {
          text: 'type',
          value: 'type',
        },
        {
          text: 'name',
          value: 'name',
        },
        {
          text: 'description',
          value: 'description',
        },
        {
          text: 'sample lvl',
          value: 'sample_level',
        },
        {
          text: 'default sample size',
          value: 'default_sample_size',
        },
        {
          text: 'party',
          value: 'party',
          hasSlot: true,
        },
        {
          text: 'delivery',
          value: 'delivery_folder',
          hasSlot: true,
        },
        {
          text: '',
          value: 'actions',
          align: 'right',
        },
      ],
      wkbMeasurementItem: {
        type: 'leanform',
      },
      wkbMeasurementDialogDisplayed: false,
      wkbMeasurementDeleteDialogDisplayed: false,
      rules: {
        required: (value) => !!value || 'Required.',
      },
      measurementSearch: '',
    };
  },
  computed: {
    ...mapGetters([
      'wkbLibraryMeasurements',
      'wkbLibraryTableStatus',
      'wkbLibraryMeasurementsForms',
    ]),

    deliveryFolders() {
      return [
        ...new Set(this.wkbLibraryMeasurements.map((m) => m.delivery_folder)),
      ].sort();
    },

    filteredMeasurements() {
      let measurements = [...this.wkbLibraryMeasurements];

      if (this.measurementSearch) {
        measurements = measurements.filter((item) =>
          item.name.toLowerCase().includes(this.measurementSearch.toLowerCase())
        );
      }

      return measurements.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    closeWKBMeasurementDialog() {
      this.wkbMeasurementItem = Object.assign(
        {},
        {
          type: 'leanform',
        }
      );
      this.wkbMeasurementDialogDisplayed = false;
      this.wkbMeasurementDeleteDialogDisplayed = false;
      this.$refs['create-wkb-measurement-form'].reset();
    },
    setupEdit(record) {
      this.wkbMeasurementItem = Object.assign({}, record);
      this.wkbMeasurementDialogDisplayed = true;
    },
    setupDelete(record) {
      this.wkbMeasurementItem = Object.assign({}, record);
      this.wkbMeasurementDeleteDialogDisplayed = true;
    },
    saveOrUpdateWKBMeasurement() {
      if (this.$refs['create-wkb-measurement-form'].validate()) {
        if (this.wkbMeasurementItem.id) {
          // update item
          const recordId = this.wkbMeasurementItem.id;
          delete this.wkbMeasurementItem.id;
          this.$store
            .dispatch('updateWkbLibraryRecord', {
              table: 'CFFA_DHME_WKB_MEASUREMENTS',
              recordId,
              body: {
                record: this.wkbMeasurementItem,
              },
            })
            .then(() => {
              this.closeWKBMeasurementDialog();
            });
        } else {
          // create item
          this.$store
            .dispatch('createWkbLibraryRecord', {
              table: 'CFFA_DHME_WKB_MEASUREMENTS',
              body: {
                record: this.wkbMeasurementItem,
              },
            })
            .then(() => {
              this.closeWKBMeasurementDialog();
            });
        }
      }
    },
    deleteWkbMeasurementRecord() {
      this.$store
        .dispatch('deleteWkbLibraryRecord', {
          table: 'CFFA_DHME_WKB_MEASUREMENTS',
          recordId: this.wkbMeasurementItem.id,
        })
        .then(() => {
          this.closeWKBMeasurementDialog();
        });
    },
  },
};
</script>

<style scoped></style>
